import type { LngLat, YMap, YMapControls } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'
import type { ClusterFeatureEntity, DispatcherWorkplaceDataEntity, DispatcherWorkplaceDelivery, DispatcherWorkplaceTake } from '@/modules/DispatcherWorkplaceModule/types'
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'

import { reactive, ref } from 'vue'

import { dispatcherWorkplaceAPI } from '@/modules/DispatcherWorkplaceModule/services'
import { getAllDispatcherSectors } from '@/new-services/dispatcher-sectors.service'

import DW_CONSTANTS from '@/modules/DispatcherWorkplaceModule/constants'

import mapHint from '@/modules/YandexMap3Module/map-hint'
import dwMapHelpers from '@/modules/DispatcherWorkplaceModule/dw-map-helpers'

import useSectorsHelper from './DispatcherWorkplaceUseCaseHelpers/sectors.helper'
import useCouriersHelper from './DispatcherWorkplaceUseCaseHelpers/couriers.helper'
import useOrdersHelper from './DispatcherWorkplaceUseCaseHelpers/orders.helper'
import useProblemsHelper from './DispatcherWorkplaceUseCaseHelpers/problems.helper'
import useFeaturePopupsHelper from './DispatcherWorkplaceUseCaseHelpers/feature-popups.helper'

// import useIntervalController from './DispatcherWorkplaceUseCaseHelpers/interval-controller'

const DEFAULT_DISPATCHER_SECTOR_ID = 24
// const INTERVAL_UPDATE_DELAY = 5 * 60 * 1000

export default function() {
    let mapInstance: YMap | null = null
    let controlsGroup: YMapControls | null = null

    const reloadDispatcherWorkplace = () => {
        sectors.destroyModule()

        couriers.destroyModule({ resetStatement: false })
        orders.destroyModule({ resetStatement: false })
        problems.destroyModule({ resetStatement: false })

        loadDispatcherWorkplace()
    }

    // const intervalController = useIntervalController(reloadDispatcherWorkplace, INTERVAL_UPDATE_DELAY)

    // {
    //     onOpenCallback: intervalController.stop,
    //     onCloseCallback: intervalController.start
    // }
    const featurePopups = useFeaturePopupsHelper()

    const state = reactive({
        is_loading: false,
        is_failed: false,
        dispatcherSectorId: null as number | null,
        date: new Date(Date.now()).toISOString().split('T')[0],
        activeTab: DW_CONSTANTS.COURIERS_TAB.id,
        is_self_pickup_list_popup_opened: false,
        is_self_delivery_list_popup_opened: false,
        selectedUnassignedTakes: [] as number[],
        is_mass_appointment_popup_opened: false,
    })

    const selectedFeatureId = ref<number | null>(null)

    const dispatcher_sectors_state = reactive({
        is_loading: false,
        is_failed: false,
        list: [] as DispatcherSector[]
    })

    const statement = reactive({
        is_opened: true,
        is_loading: true,
        is_failed: false,
        loadingMessage: 'Инициализация движка карты...' as string | null,
    })

    const updateStatement = (message: string) => {
        statement.loadingMessage = message
    }

    const sectors = useSectorsHelper()
    const orders = useOrdersHelper({ openPopup: featurePopups.openPopup, selectedFeatureId })
    const couriers = useCouriersHelper({ openPopup: featurePopups.openPopup, selectedFeatureId })
    const problems = useProblemsHelper({ openPopup: featurePopups.openPopup, selectedFeatureId })

    orders.setQuickFiltersCallbacks({
        onUndeliveredDeliveriesChanged: couriers.setUndeliveredDeliveriesQuckFilters,
        onUnassignedTakesChanged: (is_enabled: boolean) => {
            state.selectedUnassignedTakes = []
            couriers.setUnassignedTakesQuckFilters(is_enabled)
        }
    })

    couriers.setQuickFiltersCallbacks({
        onUndeliveredDeliveriesChanged: orders.setUndeliveredDeliveriesQuckFilters,
        onUnassignedTakesChanged: (is_enabled: boolean) => {
            state.selectedUnassignedTakes = []
            orders.setUnassignedTakesQuckFilters(is_enabled)
        }
    })

    const onOpenSelfPickupListPopup = () => {
        // intervalController.stop()
        state.is_self_pickup_list_popup_opened = true
    }

    const onCloseSelfPickupListPopup = () => {
        // intervalController.start()
        state.is_self_pickup_list_popup_opened = false
    }

    const onOpenSelfDeliveryListPopup = () => {
        // intervalController.stop()
        state.is_self_delivery_list_popup_opened = true
    }

    const onCloseSelfDeliveryListPopup = () => {
        // intervalController.start()
        state.is_self_delivery_list_popup_opened = false
    }

    const toggleUnassignedTake = (take_id: number) => {
        if (state.selectedUnassignedTakes.includes(take_id)) {
            const index = state.selectedUnassignedTakes.findIndex((take) => take === take_id)

            if (index >= 0) {
                state.selectedUnassignedTakes.splice(index, 1)
            }
        } else {
            // intervalController.stop()
            state.selectedUnassignedTakes.push(take_id)
        }

        if (state.selectedUnassignedTakes.length === 0) {
            // intervalController.start()
        }
    }

    const onMassAppointment = () => {
        state.is_mass_appointment_popup_opened = true
    }

    const onCloseMassAppointment = () => {
        state.is_mass_appointment_popup_opened = false
    }

    const onMassAppointmentSubmited = () => {
        onCloseMassAppointment()

        state.selectedUnassignedTakes = []

        sectors.destroyModule()
        couriers.destroyModule({ resetStatement: true })
        orders.destroyModule({ resetStatement: true })
        problems.destroyModule({ resetStatement: true })

        loadDispatcherWorkplace()
    }

    const loadDispatcherSectors = async () => {
        try {
            updateStatement('Загружаем сектора диспетчера...')
            dispatcher_sectors_state.is_loading = true

            const response = await getAllDispatcherSectors() 
            dispatcher_sectors_state.list = response.data.data

            dispatcher_sectors_state.is_failed = false
            onDispatcherSectorChanged(DEFAULT_DISPATCHER_SECTOR_ID)
        } catch (error) {
            console.error(error)
            dispatcher_sectors_state.is_failed = true
        } finally {
            dispatcher_sectors_state.is_loading = false
        }
    }

    const onDateChanged = (date: string) => {
        state.date = date
        
        sectors.destroyModule()
        couriers.destroyModule({ resetStatement: true })
        orders.destroyModule({ resetStatement: true })
        problems.destroyModule({ resetStatement: true })

        loadDispatcherWorkplace()
    }

    const onDispatcherSectorChanged = (dispatcher_sector_id: number) => {
        state.dispatcherSectorId = dispatcher_sector_id

        sectors.destroyModule()
        couriers.destroyModule({ resetStatement: true })
        orders.destroyModule({ resetStatement: true })
        problems.destroyModule({ resetStatement: true })

        loadDispatcherWorkplace()

        const dispatcherSector = dispatcher_sectors_state.list.find((dispatcher_sector) => dispatcher_sector.id === dispatcher_sector_id)

        if (dispatcherSector && dispatcherSector.city) {
            dwMapHelpers.updateMapLocation({
                map: mapInstance as YMap,
                coordinates: [dispatcherSector.city.longitude, dispatcherSector.city.latitude] as LngLat,
                zoom: 12
            })
        }
    }

    const onModuleSwitched = (module_id: string) => {
        state.activeTab = module_id
        state.selectedUnassignedTakes = []

        couriers.destroyModule({ resetStatement: false })
        orders.destroyModule({ resetStatement: false })
        problems.destroyModule({ resetStatement: false })

        if (state.activeTab === DW_CONSTANTS.COURIERS_TAB.id) {
            couriers.initializeModule({
                mapInstance: mapInstance as YMap,
                controlsGroup: controlsGroup as YMapControls,
            })

            orders.initializeModule({
                mapInstance: mapInstance as YMap,
                controlsGroup: controlsGroup as YMapControls,
                forCouriers: true
            })
        }

        if (state.activeTab === DW_CONSTANTS.ORDERS_TAB.id) {
            orders.initializeModule({
                mapInstance: mapInstance as YMap,
                controlsGroup: controlsGroup as YMapControls,
                forCouriers: false,
            })
        }

        if (state.activeTab === DW_CONSTANTS.PROBLEMS_TAB.id) {
            problems.initializeModule({
                mapInstance: mapInstance as YMap,
                controlsGroup: controlsGroup as YMapControls
            })
        }
    }

    const onDispatcherWorkplaceLoaded = () => {
        updateStatement('Настраиваем карту...')

        yandexCreateControlsGroup()
        yandexInstallModules()

        onModuleSwitched(state.activeTab)

        statement.is_opened = false
        statement.is_loading = false
        statement.loadingMessage = null

        // intervalController.start()
    }

    const loadDispatcherWorkplace = async () => {
        updateStatement('Загрузка данных...')

        try {
            statement.is_opened = true
            statement.is_loading = true

            state.is_loading = true

            const response = await dispatcherWorkplaceAPI.getDispatcherWorkplace({
                date: state.date as string,
                dispatcherSectorId: state.dispatcherSectorId as number
            })

            couriers.updateList(response.data.data.couriers)
            orders.updateList(response.data.data.items)
            problems.updateList(response.data.data.problems.filter((problem) => {
                if (problem.type === 1 && (problem as DispatcherWorkplaceTake).notAssigned) {
                    return false
                }

                if (problem.type === 2 && (problem as DispatcherWorkplaceDelivery).notDelivered) {
                    return false
                }

                return true
            }))

            await sectors.initializeModule({
                mapInstance: mapInstance as YMap,
                dispatcherSectorId: state.dispatcherSectorId as number
            })

            onDispatcherWorkplaceLoaded()
        } catch (error) {
            console.error(error)

            state.is_failed = true

            statement.is_loading = false
            statement.loadingMessage = null
            statement.is_failed = true
        } finally {
            state.is_loading = false
        }
    }

    const yandexInstallModules = () => {
        ymaps3.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
            '@yandex/ymaps3-hint@0.0.9',
            '@yandex/ymaps3-clusterer@0.0.10'
        ])

        mapHint.addHintToMap(mapInstance as YMap)
    }

    const yandexCreateControlsGroup = () => {
        if (!mapInstance) return

        const { YMapControls } = ymaps3
        controlsGroup = new YMapControls({ position: 'top right' })
        mapInstance.addChild(controlsGroup)
    }

    const yandexOnMapReady = (map: YMap) => {
        mapInstance = map
        mapInstance.setBehaviors(['scrollZoom', 'drag'])
        loadDispatcherSectors()
    }

    const yandexCenterMapByDataEntity = (record: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(record.longitude as string), Number(record.latitude as string)],
            zoom: 17
        })
    }

    const onOrderDateChanged = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        featurePopups.closePopup()
        reloadDispatcherWorkplace()
    }

    const onOrderAddedToWaitList = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        featurePopups.closePopup()
        reloadDispatcherWorkplace()
    }

    const onOrderAssigned = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        featurePopups.closePopup()
        reloadDispatcherWorkplace()
    }

    const onOrderSelfDelivery = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        featurePopups.closePopup()
        reloadDispatcherWorkplace()
    }

    const onSidebarEntityClicked = (dataEntity: DispatcherWorkplaceDataEntity, type: 'couriers' | 'orders' | 'problems') => {
        if (dataEntity.type === 1) {
            selectedFeatureId.value = (dataEntity as DispatcherWorkplaceTake).orderId
        }

        if (dataEntity.type === 2) {
            selectedFeatureId.value = (dataEntity as DispatcherWorkplaceDelivery).invoiceId
        }

        if (type === 'couriers') {
            couriers.onCourierEntityClicked(dataEntity)
        }

        if (type === 'orders') {
            orders.onEntityClicked(dataEntity)
        }

        if (type === 'problems') {
            problems.onEntityClicked(dataEntity)
        }
    }

    return {
        DW_CONSTANTS,
        // intervalController,
        featurePopups,
        state,
        dispatcher_sectors_state,
        statement,
        sectors,
        couriers,
        orders,
        problems,
        onOpenSelfPickupListPopup,
        onCloseSelfPickupListPopup,
        onOpenSelfDeliveryListPopup,
        onCloseSelfDeliveryListPopup,
        toggleUnassignedTake,
        onDateChanged,
        onDispatcherSectorChanged,
        onModuleSwitched,
        yandexOnMapReady,
        yandexCenterMapByDataEntity,
        onOrderDateChanged,
        onOrderAddedToWaitList,
        onOrderAssigned,
        onOrderSelfDelivery,
        onMassAppointment,
        onCloseMassAppointment,
        onMassAppointmentSubmited,
        onSidebarEntityClicked,
        reloadDispatcherWorkplace
    }
}